<ng-container *transloco="let t; read: 'devices'">
  <app-grid-page [title]="t('title')">
    <app-grid
      [columns]="columns()"
      [data]="devices()"
      [rowId]="deviceId"
      [loading]="isLoading()"
      [page]="page()"
      [pageSize]="size()"
      [totalElements]="totalElements()"
      [totalPages]="totalPages()"
      [sort]="sort()"
      [searchable]="true"
      [exportable]="true"
      [detailEnabled]="true"
      [primaryAction]="{ label: t('addDevice'), icon: 'icon-content-add24px' }"
      [rowActions]="rowActions()"
      (sortChange)="onSortChange($event)"
      (pageChange)="onPageChange($event)"
      (pageSizeChange)="onPageSizeChange($event)"
      (rowClick)="onRowClick(asDevice($event))"
      (rowDblClick)="onRowDblClick(asDevice($event))"
      (rowActionClick)="onRowAction($event.actionId, asDevice($event.row))"
      (primaryActionClick)="openAddDeviceDialog()"
    >
      <ng-template gridDetail let-row>
        @let device = asDevice(row);
        <div class="relative mb-4 flex h-40 w-full items-center justify-center overflow-hidden rounded-card bg-sunken">
          @if (isLoadingLatestSnapshot() && !displaySnapshot()) {
            <app-spinner [size]="20" class="text-ink-3" />
          } @else if (displaySnapshot() && latestSnapshotImageUrl()) {
            <img
              [src]="latestSnapshotImageUrl()"
              [alt]="t('detail.latestImageAlt')"
              class="h-full w-full object-cover"
            />
            @if (latestSnapshot()?.status === 'PENDING') {
              <span class="absolute bottom-1.5 right-1.5 flex items-center gap-1 rounded-full bg-scrim-control px-2 py-0.5 text-[11px] text-bar-ink">
                <app-spinner [size]="12" />
                {{ t('detail.latestImagePending') }}
              </span>
            }
          } @else if (latestSnapshot()?.status === 'PENDING') {
            <span class="text-[13px] text-ink-3">{{ t('detail.latestImagePending') }}</span>
          } @else if (latestSnapshot()?.status === 'FAILED') {
            <span class="text-[13px] text-danger">{{ t('detail.latestImageFailed') }}</span>
          } @else {
            <span class="text-[13px] text-ink-3">{{ t('detail.latestImageNone') }}</span>
          }
        </div>
        <div class="font-display text-xl font-bold text-ink-1">{{ device.clientId }}</div>
        <div class="mb-4 text-sm text-ink-3">{{ device.serialNumber || t('columns.serialNumber') }}</div>

        <div class="border-t border-hairline">
          <div class="flex items-center justify-between border-b border-hairline py-3">
            <span class="text-[13px] text-ink-3">{{ t('detail.connectivity') }}</span>
            <span class="inline-flex items-center gap-2 text-[13px] font-semibold text-ink-1">
              <span class="h-2.5 w-2.5 rounded-full" [class.bg-success]="device.online" [class.bg-danger]="!device.online"></span>{{ device.online ? t('status.online') : t('status.offline') }}
            </span>
          </div>
          <div class="flex items-center justify-between border-b border-hairline py-3">
            <span class="text-[13px] text-ink-3">{{ t('detail.provisioning') }}</span>
            <span class="text-[13px] font-semibold text-ink-1">
              {{
                device.waitingForReset
                  ? t('status.awaitingReset')
                  : device.isProvisioned
                    ? t('status.provisioned')
                    : t('status.unprovisioned')
              }}
            </span>
          </div>
          <div class="flex items-center justify-between border-b border-hairline py-3">
            <span class="text-[13px] text-ink-3">{{ t('columns.type') }}</span>
            <span class="text-[13px] font-semibold text-ink-1">{{ device.type || t('na') }}</span>
          </div>
          <div class="flex items-center justify-between border-b border-hairline py-3">
            <span class="text-[13px] text-ink-3">{{ t('columns.vendorId') }}</span>
            <span class="text-[13px] font-semibold text-ink-1">{{ device.vendorId || t('na') }}</span>
          </div>
          <div class="flex items-center justify-between border-b border-hairline py-3">
            <span class="text-[13px] text-ink-3">{{ t('detail.tenantId') }}</span>
            <span class="text-[13px] font-semibold text-ink-1">{{ device.tenantId || t('na') }}</span>
          </div>
          <div class="flex items-center justify-between border-b border-hairline py-3">
            <span class="text-[13px] text-ink-3">{{ t('detail.deviceConfigurationId') }}</span>
            <span class="text-[13px] font-semibold text-ink-1">{{ device.deviceConfigurationId || t('na') }}</span>
          </div>
          <div class="flex items-center justify-between border-b border-hairline py-3">
            <span class="text-[13px] text-ink-3">{{ t('detail.localities') }}</span>
            <span class="text-[13px] font-semibold text-ink-1">{{ localitiesText(device) }}</span>
          </div>
          <div class="flex items-center justify-between border-b border-hairline py-3">
            <span class="text-[13px] text-ink-3">{{ t('detail.provisioningUser') }}</span>
            <span class="text-[13px] font-semibold text-ink-1">{{ device.provisioningUser || t('na') }}</span>
          </div>
          <div class="flex items-center justify-between border-b border-hairline py-3">
            <span class="text-[13px] text-ink-3">{{ t('detail.lastUpdated') }}</span>
            <span class="text-[13px] font-semibold text-ink-1">{{ device.lastUpdated | date: 'medium' }}</span>
          </div>
        </div>

        <div class="mt-5 grid grid-cols-2 gap-2">
          <app-button size="sm" [label]="t('actions.edit')" (activated)="editSelectedDevice()" />
          <app-button variant="secondary" size="sm" [label]="t('actions.assignContent')" (activated)="openAssignContentDialog()" />
          <app-button variant="secondary" size="sm" [label]="t('actions.status')" (activated)="requestStatusForSelectedDevice()" />
          <app-button class="col-span-2" variant="secondary" size="sm" destructive [label]="t('actions.reset')" (activated)="resetSelectedDevice()" />
          <app-button variant="primary" size="sm" destructive [label]="t('actions.remove')" (activated)="openRemoveModal(device)" />
        </div>
      </ng-template>
    </app-grid>
  </app-grid-page>

  @if (isAssignContentDialogOpen() && selectedDevice()) {
    <app-assign-content-dialog
      [device]="selectedDevice()!"
      (close)="isAssignContentDialogOpen.set(false)"
    ></app-assign-content-dialog>
  }

  @if (isAddDialogOpen()) {
    <app-add-device-dialog
      [provisionedDeviceInput]="selectedDevice()"
      [busy]="isSavingDevice()"
      (deviceAdded)="handleDeviceAdded($event)"
      (dialogClosed)="closeAddDeviceDialog()"
    ></app-add-device-dialog>
  }

  @if (devicePendingRemoval(); as pending) {
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-scrim p-4"
      (click)="closeRemoveModal()"
      aria-hidden="true"
    >
      <div
        class="flex w-[420px] max-w-full flex-col gap-3.5 rounded-card bg-card p-7 shadow-overlay"
        role="dialog"
        aria-modal="true"
        (click)="$event.stopPropagation()"
      >
        <span class="text-lg font-bold">{{ t('removeModal.title', { id: pending.clientId }) }}</span>
        <p class="m-0 text-sm leading-relaxed text-ink-2">{{ t('removeModal.body') }}</p>
        <div class="mt-1.5 flex justify-end gap-2.5">
          <button
            type="button"
            (click)="closeRemoveModal()"
            [disabled]="isRemoving()"
            class="rounded-control border border-strong bg-card px-4 py-2 text-sm font-semibold hover:bg-sunken disabled:opacity-60"
          >
            {{ t('removeModal.cancel') }}
          </button>
          <button
            type="button"
            (click)="confirmRemove()"
            [disabled]="isRemoving()"
            class="rounded-control bg-danger px-4 py-2 text-sm font-semibold text-action-text hover:opacity-90 disabled:opacity-60"
          >
            {{ t('actions.remove') }}
          </button>
        </div>
      </div>
    </div>
  }
</ng-container>
